import { Alert, Button, Container, Icon, Stack } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AccountService, UserRole } from "../../api/identity";
import { UserService, UserStatus } from "../../api/psc";
import { ErrorDetailContainer, toErrorDetailContainer } from "../../api/config";
import ErrorParser from "../../components/shared/ErrorParser";
import { useAppSelector } from "../../hooks/reduxHooks";
import { HelpIconButton, PageHeader, Spinner } from "../../components/exports";
import { UserContext } from "../../contexts/UserContext";
import User, { ManagedUser } from "./User";

// updated
export default function ListUsers() {
    const navigate = useNavigate();

    const [users, setUsers] = useState<ManagedUser[] | null>(null);
    const [error, setError] = useState<ErrorDetailContainer | null>(null);
    const [pendingUserId, setPendingUserId] = useState<number | null>(null);
    const maxUserCount = useAppSelector(state => state.shared.accountDetail?.maxUserCount);

    const user = useContext(UserContext);

    const isOwner = user.role === 1;

    useEffect(() => {
        if (isOwner) loadUsers();
    }, [isOwner]);

    const loadUsers = async () => {
        setError(null);
        try {
            const [identityUsers, activationUsers] = await Promise.all([
                AccountService.listUsers(), UserService.listUsers("1"),
            ]);
            const statuses = new Map(activationUsers.map(u => [u.id, u]));
            setUsers(identityUsers.map(u => ({ ...u,
                activationStatus: statuses.get(u.id)?.status,
            })));
        } catch (e) {
            setError(toErrorDetailContainer(e));
        }
    };

    const changeActivation = async (selectedUser: ManagedUser, active: boolean) => {
        if (pendingUserId !== null) return;
        setPendingUserId(selectedUser.id);
        setError(null);
        try {
            if (active) await UserService.activateUser(selectedUser.id, "1");
            else await UserService.deactivateUser(selectedUser.id, "1");
            setUsers(current => current.map(u => u.id === selectedUser.id
                ? { ...u, activationStatus: active ? UserStatus.ACTIVE : UserStatus.DISABLED } : u));
        } catch (e) {
            const activationError = toErrorDetailContainer(e);
            setError(activationError.hasError("UserNotActive")
                ? ErrorDetailContainer.fromString("ظرفیت کاربران فعال تکمیل شده است. ابتدا یکی از کاربران را غیرفعال کنید.")
                : activationError);
        } finally {
            setPendingUserId(null);
        }
    };

    const goToCreateUser = () => {
        navigate(`/user/create`);
    };

    if (isOwner && !users && !error) {
        return <Spinner />;
    }

    return (
        <Container maxWidth="sm">
            <PageHeader
                title="لیست اپراتور ها"
                action={
                    <Stack direction="row" spacing={1} alignItems="center">
                        <HelpIconButton tag="manage_users" />
                        <Button
                            onClick={goToCreateUser}
                            variant="contained"
                            disabled={!isOwner}
                            startIcon={<Icon>add</Icon>}
                        >
                            افزودن کاربر
                        </Button>
                    </Stack>
                }
            />

            {!isOwner && (
                <Alert severity="error">
                    تنها صاحب حساب کاربری به بخش مدیریت کاربران دسترسی دارد
                </Alert>
            )}

            {error && <ErrorParser error={error} />}
            {isOwner && !users && error && <Button onClick={loadUsers}>تلاش مجدد</Button>}

            {isOwner && users && (
                <Stack alignItems="center" spacing={2}>
                    <Alert severity="info" sx={{ width: "100%" }}>
                        کاربران فعال (با احتساب صاحب حساب): {users.filter(u => u.activationStatus === UserStatus.ACTIVE || u.role === UserRole.ADMINISTRATOR).length}
                        {maxUserCount != null && ` از ${maxUserCount}`}.
                        {" "}برای فعال کردن کاربر جدید در صورت تکمیل ظرفیت، ابتدا یکی از کاربران را غیرفعال کنید.
                    </Alert>
                    {users.map((u) => (
                        <User key={u.id} user={u} pending={pendingUserId !== null} onActivationChange={changeActivation} />
                    ))}
                </Stack>
            )}
        </Container>
    );
}
